import {SearchResultRecord} from './model/search-result-record.model';
import {AutocompleteNode} from './model/autocomplete-node.model';
import {SearchResult} from './model/search-result.model';
import {AutocompleteRecommendationDomInjector} from './autocomplete-recommendation/autocomplete-recommendation-dom-injector';
import {Autocomplete} from './autocomplete.component';
import {
    fromEvent,
    map,
    debounceTime,
    distinctUntilChanged,
    tap,
    switchMap,
    filter,
    shareReplay,
    Observable,
    BehaviorSubject
} from 'rxjs';

export class AutocompleteDomInjector {
    autocompleteNodes: AutocompleteNode[] = [];

    private inputElement: HTMLInputElement = this.hostElement.querySelector('.search-box-input') as HTMLInputElement;
    private autocompleteContainer: HTMLElement = this.hostElement.querySelector('.autocomplete-container') as HTMLInputElement;
    private nodesFocusStream = new BehaviorSubject<AutocompleteNode>(null);


    private inputStream: Observable<string> = fromEvent(this.hostElement, 'input').pipe(
        map(() => this.inputElement.value)
    );

    private inpKeyDownStream: Observable<KeyboardEvent> = fromEvent<KeyboardEvent>(this.inputElement, 'keydown');
    private inpArrowDownStream: Observable<KeyboardEvent> = this.inpKeyDownStream.pipe(
        filter((event: KeyboardEvent) => event.key === 'ArrowDown'),
        tap((event: KeyboardEvent) => {
            event.preventDefault();
            this.handleInpArrowDown()
        })
    );

    private recKeyDownStream: Observable<KeyboardEvent> = fromEvent<KeyboardEvent>(this.autocompleteContainer, 'keydown');
    private recArrowStream: Observable<KeyboardEvent> = this.recKeyDownStream.pipe(
        filter((event: KeyboardEvent) => (event.key === 'ArrowUp' || event.key === 'ArrowDown')),
        tap((event: KeyboardEvent) => {
            event.preventDefault();
            if (event.key === 'ArrowUp') {
                this.handleRecArrowUp();
                return;
            }

            this.handleRecArrowDown();
        })
    );

    private recBlurStream: Observable<Event> = fromEvent(this.autocompleteContainer, 'blur').pipe(
        tap(() => this.nodesFocusStream.next(null))
    );

    private autocompleteStream: Observable<SearchResultRecord[]> = this.inputStream.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        tap(() => {
            this.reset();
        }),
        filter((q: string) => q.length >= this.autocomplete.config.minCharacters),
        switchMap((q: string) =>
            this.autocomplete.getSearchResult(q).pipe(
                map((searchResult: SearchResult) => searchResult.searchResultItems)
            )
        ),
        filter((searchResults: SearchResultRecord[]) =>
            searchResults.length > 0 &&
            searchResults.length <= this.autocomplete.config.maxResults
        ),
        tap((searchResults: SearchResultRecord[]) => {
            this.appendAutocompleteNodes(searchResults);
            this.showAutocompleteContainer();
        }),
        shareReplay(1)
    );

    constructor(
        private document: Document,
        private window: Window,
        private hostElement: HTMLElement,
        private autocomplete: Autocomplete
    ) {
        this.nodesFocusStream.subscribe();
        this.autocompleteStream.subscribe();
        this.inpArrowDownStream.subscribe();
        this.recArrowStream.subscribe();
        this.recBlurStream.subscribe();
    }

    reset(): void {
        this.hideAutocompleteContainer();
        this.removeAutocompleteNodes();
    }

    empty(): void {
        this.inputElement.value = '';
        this.reset();
    }

    private appendAutocompleteNodes(searchResultRecords: SearchResultRecord[]): void {
        let i = 0;
        for (const searchResultRecord of searchResultRecords) {
            const autocompleteNode = new AutocompleteNode();
            autocompleteNode.recommendation = searchResultRecord;
            autocompleteNode.domInjector = new AutocompleteRecommendationDomInjector(
                this.document,
                this.window,
                this.autocompleteContainer,
                autocompleteNode
            );
            autocompleteNode.index = i;
            autocompleteNode.nodesFocusStream = this.nodesFocusStream;
            this.autocompleteNodes.push(autocompleteNode);
            i++;
        }
    }

    private removeAutocompleteNodes(): void {
        for (const autocompleteNode of this.autocompleteNodes) {
            autocompleteNode.domInjector.destroy();
        }

        this.autocompleteNodes = [];
    }

    private hideAutocompleteContainer(): void {
        this.autocompleteContainer.classList.add('hide');
        this.autocomplete.config.toggleDisplay(false);
        this.inputElement.setAttribute('aria-expanded', 'false');
    }

    private showAutocompleteContainer(): void {
        this.autocompleteContainer.classList.remove('hide');
        this.autocomplete.config.toggleDisplay(true);
        this.inputElement.setAttribute('aria-expanded', 'true');
    }

    private handleInpArrowDown(): void {
        if (this.autocompleteNodes.length === 0) {
            return;
        }

        this.autocompleteNodes[0].domInjector.focus();
    }

    private handleRecArrowUp(): void {
        if (this.nodesFocusStream.getValue() === null) {
            this.autocompleteNodes[this.nodesFocusStream.getValue().index - 1].domInjector.focus();
            return;
        }

        if (this.nodesFocusStream.getValue().index === 0) {
            this.inputElement.focus();
            this.nodesFocusStream.next(null);
            return;
        }

        this.autocompleteNodes[this.nodesFocusStream.getValue().index - 1].domInjector.focus();
    }

    private handleRecArrowDown(): void {
        if (this.nodesFocusStream.getValue() === null) {
            this.autocompleteNodes[0].domInjector.focus();
            return;
        }

        if (this.nodesFocusStream.getValue().index === this.autocompleteNodes.length - 1) {
            this.autocompleteNodes[0].domInjector.focus();
            return;
        }

        this.autocompleteNodes[this.nodesFocusStream.getValue().index + 1].domInjector.focus();
    }
}